// The tool bar under the header (.filter-bar, sticky): a screen renders <ShellToolbar> anywhere in its tree and the
// content appears in the bar; the bar hides itself when no screen uses it (mockup def.toolbar).
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useFmt, useT } from '../i18n';
import { MethodSeg, PeriodSeg } from '../ui/DateRange';
import { Icon } from '../ui/Icon';

interface ToolbarSlot {
  el: HTMLElement | null;
  register(): () => void;
}
export const ToolbarSlotContext = createContext<ToolbarSlot>({ el: null, register: () => () => {} });

/** Put content into the shell's tool bar while this component is mounted. */
export function ShellToolbar({ children }: { children: ReactNode }) {
  const slot = useContext(ToolbarSlotContext);
  useEffect(() => slot.register(), [slot]);
  return slot.el ? createPortal(children, slot.el) : null;
}

/** State for the shell: the bar element and how many screens use it. */
export function useToolbarSlot(): [ToolbarSlot, (el: HTMLElement | null) => void, boolean] {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [count, setCount] = useState(0);
  const [slot, setSlot] = useState<ToolbarSlot>({ el: null, register: () => () => {} });
  useEffect(() => {
    setSlot({
      el,
      register: () => {
        setCount((n) => n + 1);
        return () => setCount((n) => Math.max(0, n - 1));
      },
    });
  }, [el]);
  return [slot, setEl, count > 0];
}

/**
 * The common tool bar of home / activity / statistics / people (mockup toolbar of those screens):
 * 기간 [오늘|7일|30일|90일|직접] · (사용 방식 [전체|CLI 도구|API 키 호출]) · 기준 시각. `children` adds screen controls.
 */
export function CommonToolbar({ method = false, asOf, children, className }: { method?: boolean; asOf?: number; children?: ReactNode; className?: string }) {
  const t = useT();
  const fmt = useFmt();
  return (
    <div className={'toolbar' + (className ? ' ' + className : '')}>
      <span className="toolbar__label" aria-hidden="true">
        {t('period.label')}
      </span>
      <PeriodSeg />
      {method ? <MethodSeg /> : null}
      {children}
      <span className="toolbar__meta">
        <Icon name="clock" />
        {t('period.asOf', { time: fmt.full(asOf ?? Date.now()) })}
      </span>
    </div>
  );
}
